import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AppSidebar } from '@/components/app-sidebar'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { EmailVerificationBanner } from '@/components/auth/email-verification-banner'
import { useWorkspaceStore } from '@/stores/workspace-store'
import { WorkspaceDialog } from '@/components/workspace/workspace-dialog'
import { DocumentEditor } from '@/components/document/document-editor'
import { Plus, FileText, X, FolderOpen } from 'lucide-react'

// AllDocsView component
function AllDocsView({ workspaceId }: { workspaceId: string }) {
  const { documents, loading, createDocument } = useWorkspaceStore()
  const [creatingDoc, setCreatingDoc] = useState(false)

  const handleCreateDocument = async () => {
    setCreatingDoc(true)
    try {
      await createDocument(workspaceId)
    } catch (error) {
      console.error('Failed to create document:', error)
    } finally {
      setCreatingDoc(false)
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">All Documents</h2>
        <Button 
          onClick={handleCreateDocument} 
          disabled={creatingDoc}
          size="sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          {creatingDoc ? 'Creating...' : 'New Doc'}
        </Button>
      </div>

      {loading.documents ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-muted-foreground">Loading documents...</div>
        </div>
      ) : documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No documents yet</h3>
          <p className="text-muted-foreground mb-4">Create your first document to get started</p>
          <Button onClick={handleCreateDocument} disabled={creatingDoc}>
            <Plus className="h-4 w-4 mr-2" />
            {creatingDoc ? 'Creating...' : 'Create Document'}
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {documents.map((doc) => (
            <div 
              key={doc.id}
              className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => useWorkspaceStore.getState().openDocument(doc)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FileText className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div>
                    <h3 className="font-medium">{doc.title}</h3>
                    {doc.summary && (
                      <p className="text-sm text-muted-foreground mt-1">{doc.summary}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <span>Updated {new Date(doc.updatedAt).toLocaleDateString()}</span>
                      {doc.wordCount && <span>• {doc.wordCount} words</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {doc.isFavorite && <Badge variant="secondary">Favorite</Badge>}
                  {doc.isPublic && <Badge variant="outline">Public</Badge>}
                  {doc.isTemplate && <Badge variant="outline">Template</Badge>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Dashboard() {
  const { 
    workspaces, 
    currentWorkspace, 
    tabs, 
    activeTabId, 
    loading,
    fetchWorkspaces, 
    setCurrentWorkspace,
    closeTab,
    setActiveTab 
  } = useWorkspaceStore()

  useEffect(() => {
    fetchWorkspaces()
  }, [fetchWorkspaces])

  const activeTab = tabs.find(tab => tab.id === activeTabId)

  const renderTabContent = () => {
    if (!activeTab || !currentWorkspace) return null

    switch (activeTab.type) {
      case 'all-docs':
        return <AllDocsView workspaceId={currentWorkspace.id} />
      case 'document':
        if (activeTab.documentId) {
          return <DocumentEditor documentId={activeTab.documentId} />
        }
        return null
      default:
        return null
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b">
          <div className="flex items-center gap-2 px-4">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarTrigger className="-ml-1" />
                </TooltipTrigger>
                <TooltipContent className="px-2 py-1 text-xs">
                  <span className="font-mono">
                    Toggle Sidebar{' '}
                    <kbd className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      <span className="mx-1 font-bold">⌘</span>
                      <span className="mx-1">+</span>
                      <span className="mx-1">B</span>
                    </kbd>
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            
            {/* Workspace Selector */}
            <div className="flex items-center gap-2">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      {currentWorkspace?.name || 'Loading...'}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              
              <WorkspaceDialog 
                onWorkspaceCreated={fetchWorkspaces}
                trigger={
                  <Button variant="ghost" size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                }
              />
            </div>
          </div>
        </header>

        {/* Tab Bar */}
        {tabs.length > 0 && (
          <div className="flex items-center border-b bg-muted/30 px-4">
            <div className="flex items-center gap-1 py-2">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm cursor-pointer transition-colors ${
                    tab.id === activeTabId
                      ? 'bg-background border shadow-sm'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.type === 'all-docs' ? (
                    <FolderOpen className="h-4 w-4" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  <span>{tab.title}</span>
                  {tabs.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-muted-foreground/20"
                      onClick={(e) => {
                        e.stopPropagation()
                        closeTab(tab.id)
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-1 flex-col p-4">
          <EmailVerificationBanner />
          
          {loading.workspaces ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading workspace...</div>
            </div>
          ) : !currentWorkspace ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <h2 className="text-2xl font-bold mb-4">Welcome to ThoughtFabric</h2>
              <p className="text-muted-foreground mb-6 max-w-md">
                Create your first workspace to start capturing, creating, and planning.
              </p>
              <WorkspaceDialog onWorkspaceCreated={fetchWorkspaces} />
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

export const Route = createFileRoute('/dashboard/')({
  beforeLoad: async () => {
    const { useAuthStore } = await import('@/stores/auth-store')
    const { isAuthenticated } = useAuthStore.getState()

    // If user is not authenticated, redirect to login
    if (!isAuthenticated) {
      throw redirect({ to: '/auth/login' })
    }
  },
  component: Dashboard,
})
